/**
 * KIOSK 全域 session — 跨畫面的購票草稿、語言與預設字級。
 *
 * 自助機台無使用者帳號；一個「session」= 一位現場顧客的一次購票流程。
 * 閒置歸零（IdleReset）或出票完成後呼叫 resetSession() 清空草稿，回到下一位。
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import type {
  OrderSummary,
  PricingQuoteResponse,
  ProductSummary,
  RecommendItinerary,
  ScheduleSummary,
} from './api';
import { hasString, translate, type Locale } from './i18n';
import { endPassengerSession } from './passenger-session';
import type { PhoneCountry } from './phone';
import { useKioskTheme } from './theme';

export type DeliveryMethod = 'paper-qr' | 'email' | 'sms' | 'online';
export type PaymentMethod = 'credit-card' | 'apple-pay' | 'google-pay' | 'line-pay';
/*
 * `paper` 原本叫 `cert`（自然人憑證）。2026-08-19 設計稿把那個選項改成「個人
 * 一般發票」——兩者是不同的載具，沿用舊名會讓接電子發票 API 的人挑錯欄位。
 * 建單時由 `toInvoiceRequest`（lib/invoice-request.ts）換成 API 的 `invoice.kind`
 * 送出；兩邊命名不同，改這裡的值要一併改那張對照表。
 */
export type InvoiceKind = 'carrier' | 'paper' | 'donate' | 'company' | 'member_carrier';
export interface InvoiceChoice {
  kind: InvoiceKind;
  /** carrier=手機條碼 / company=統編；其餘留空。 */
  value?: string;
}

/**
 * 一段要選時段的行程：實際扣座位的商品 + 顧客為它挑的班次。
 *
 * 「一段」對應一個真實班次。單買商品只有一段（就是它自己）；套票的段是它的組成
 * 商品（套票本身不建班次）。
 */
export interface PlanLeg {
  product: ProductSummary;
  schedule: ScheduleSummary;
}

/**
 * 顧客選購的一個項目。
 *
 * 統一了兩種原本各走各的東西：套票（一個商品、多段）與行程推薦的單一商品
 * （一個商品、一段）。價格與票種一律取自 `product`——套票取的是打包價，
 * 單買取的是它自己的牌價。
 *
 * 這個結構存在的原因是行程推薦會推出多商品組合，而此前「立即訂購」只帶走
 * `products[0]`，其餘的無聲消失：顧客看到的是三小時的行程，買到的是一小時。
 */
export interface PlanItem {
  product: ProductSummary;
  legs: PlanLeg[];
}

/** 一次購票流程的累積草稿。 */
export interface OrderDraft {
  isHotelGuest: boolean | null;
  product: ProductSummary | null;
  schedule: ScheduleSummary | null;
  /** 各票種人數 {ticket_type: qty}；票種由商品定義動態決定。成人恆存且 >= 1。 */
  quantities: Record<string, number>;
  delivery: DeliveryMethod | null;
  /**
   * 顧客 email；空字串＝沒留。必不必填看店家的通知與顧客的選擇（2026-10-30，
   * 見 `emailRequiredReason`），留空就不送。
   */
  buyerEmail: string;
  /** 手機（必填，2026-10-30；顧客打的原樣）。 */
  buyerPhone: string;
  /** 手機的國碼（下拉選的）；null＝還沒選，取票頁依介面語言預設（日文→日本，其餘→台灣） */
  buyerPhoneCountry: PhoneCountry | null;
  invoice: InvoiceChoice | null;
  payment: PaymentMethod | null;
  order: OrderSummary | null;
  retryPaymentRequestId?: string;
  issuedTicketIds: string[];
  /**
   * Server-resolved 折後報價（POST /pricing/quote）；於 Schedule 完整選定後填入，
   * Delivery / Invoice / Payment 顯示之金額皆讀此處，與下單最終落庫值一致。
   */
  quote: PricingQuoteResponse | null;
  /**
   * 行程推薦選定的出發日（`YYYY-MM-DD`）。
   *
   * 留著是為了讓後續的班次頁能收斂到同一天——顧客在推薦頁答的是「8/21」，
   * 到了選班次卻看到兩週的清單，那個回答就等於白問。
   */
  recommendDate: string | null;
  /** 推薦結果；專屬行程頁讀它，直接進那一頁而沒有結果就導回問卷。 */
  recommendations: RecommendItinerary[] | null;
  /**
   * 走 `/plan`（逐段選時段）時，顧客選了哪些項目。由 Browse／行程推薦寫入；
   * 套票的段落由 `/plan` 自己查 `product_components` 展開。
   * 單一非套票商品走既有的 `/schedule`，此欄為 null。
   */
  planProducts: ProductSummary[] | null;
  /** `/plan` 選完時段後的結果；下單的 items 由它展開。 */
  plan: PlanItem[] | null;
  /**
   * `/plan` 的總額（各項目票種單價 × 人數之和）。
   *
   * 單買走 `quote`（POST /pricing/quote），但套票沒有 schedule_id 可傳給那支。
   * 這裡的單價來自 GET /pricing/ticket-types，與後端下單時取的是同一組數字，
   * 所以乘出來的總額與落庫值一致。
   */
  planTotal: number | null;
}

const EMPTY_DRAFT: OrderDraft = {
  isHotelGuest: null,
  product: null,
  schedule: null,
  quantities: { adult: 1 },
  delivery: null,
  buyerEmail: '',
  buyerPhone: '',
  buyerPhoneCountry: null,
  invoice: null,
  payment: null,
  order: null,
  issuedTicketIds: [],
  quote: null,
  recommendDate: null,
  recommendations: null,
  planProducts: null,
  plan: null,
  planTotal: null,
};

interface KioskContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  fontScale: 'A';
  draft: OrderDraft;
  patchDraft: (patch: Partial<OrderDraft>) => void;
  resetSession: (options?: { clearPayment?: boolean }) => void;
  resetGeneration: number;
  /** 付款進行中設 true：IdleReset 暫停閒置歸零（對應 Flow 3.11 不可中斷）。 */
  idleSuspended: boolean;
  setIdleSuspended: (v: boolean) => void;
  /** Printing has its own lock so voice/payment cleanup cannot release it. */
  setPrintBusy: (v: boolean) => void;
  setVoiceBusy: (v: boolean) => void;
  /** 綁定當前 locale 的譯字函式。 */
  t: (key: string, params?: Record<string, string | number>) => string;
}

const KioskContext = createContext<KioskContextValue | null>(null);

export function KioskSessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const [locale, setLocale] = useState<Locale>('zh-TW');
  const [draft, setDraft] = useState<OrderDraft>(EMPTY_DRAFT);
  const [idleSuspended, setIdleSuspended] = useState(false);
  const [printBusy, setPrintBusy] = useState(false);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [resetGeneration, setResetGeneration] = useState(0);

  const patchDraft = useCallback((patch: Partial<OrderDraft>) => {
    setDraft((d) => ({
      ...d,
      ...(patch.isHotelGuest !== undefined && patch.isHotelGuest !== d.isHotelGuest
        ? { quote: null, planTotal: null, recommendations: null }
        : {}),
      ...patch,
    }));
  }, []);

  const resetSession = useCallback((options?: { clearPayment?: boolean }) => {
    endPassengerSession(options?.clearPayment);
    setResetGeneration((value) => value + 1);
    setDraft(EMPTY_DRAFT);
    setIdleSuspended(false);
    setPrintBusy(false);
    setVoiceBusy(false);
    setLocale('zh-TW');
  }, []);

  // 吉祥物關閉時，凡是有 `.noMascot` 版本的文案一律改用它。
  //
  // 有幾句文案直接叫出角色名（「小獅船長為您安排」），那是特定業主的 IP。做在這個
  // 節點而不是各呼叫端，是因為那幾句散在四個畫面，漏掉任何一句就會讓別家公司的
  // 機台講出不屬於它的角色名 —— 而那是無人看管的機台，不會有人當場發現。
  const { mascot_enabled: mascotEnabled } = useKioskTheme();
  const t = useCallback(
    (key: string, params?: Record<string, string | number>) => {
      const alt = `${key}.noMascot`;
      const resolved = !mascotEnabled && hasString(alt) ? alt : key;
      return translate(locale, resolved, params);
    },
    [locale, mascotEnabled],
  );

  const value = useMemo<KioskContextValue>(
    () => ({
      locale,
      setLocale,
      fontScale: 'A',
      draft,
      patchDraft,
      resetSession,
      resetGeneration,
      idleSuspended: idleSuspended || printBusy || voiceBusy,
      setIdleSuspended,
      setPrintBusy,
      setVoiceBusy,
      t,
    }),
    [
      locale,
      draft,
      patchDraft,
      resetSession,
      resetGeneration,
      idleSuspended,
      printBusy,
      voiceBusy,
      t,
    ],
  );

  return <KioskContext.Provider value={value}>{children}</KioskContext.Provider>;
}

/** 取 KIOSK session context。須在 KioskSessionProvider 內使用。 */
export function useKiosk(): KioskContextValue {
  const ctx = useContext(KioskContext);
  if (ctx === null) {
    throw new Error('useKiosk must be used within KioskSessionProvider');
  }
  return ctx;
}
